/**
 * Catálogos globais — BR-24.
 *
 * 🔒 **Nenhuma destas uniões é fechada.** Account ID, serviço AWS e status são
 * catálogos administrados em runtime (`2p`, Configurações globais) e podem
 * crescer sem deploy do frontend. Fechar qualquer um deles num `z.enum` é
 * exatamente o erro que a BR-24 existe para prevenir: a tela hard-fail num
 * status que um Administrador criou hoje de manhã.
 *
 * Vêm da **API User** (`/finops/catalogos/*`), e não da API Adm. A escrita é da
 * API Adm — mas espelhar a leitura aqui evita uma segunda chamada em toda
 * montagem de tela de módulo, e evita que a regra de eslint que proíbe telas de
 * módulo de importar `apiAdm` precise de exceção.
 */
import { z } from 'zod'

import { id, valorDeCatalogo } from './comum'

export const statusDoCatalogoSchema = z.object({
  idStatus: valorDeCatalogo,
  rotulo: z.string(),
  ordem: z.number(),

  /**
   * 🔒 BR-20 como DADO. Os campos que precisam estar preenchidos para a
   * oportunidade PARAR neste status — em snake_case, porque é o vocabulário do
   * catálogo no servidor.
   *
   * O formulário `2f` usa isto para marcar o asterisco no campo certo por fase,
   * em vez de ter a regra hardcoded em React e divergir do servidor.
   */
  exigeCampos: z.array(z.string()),

  /** É isto que exclui `arquivada` do filtro padrão da `2e` — e não um
   *  `status !== 'arquivada'` espalhado pelas telas. */
  incluirNoFiltroPadrao: z.boolean(),

  /** Conta para o histograma da `2h`. Separado do id porque a iOX pode criar
   *  "implementada-parcial". */
  contaComoRealizada: z.boolean(),

  /**
   * As transições válidas a partir daqui. A UI só oferece estes botões.
   *
   * ⚠️ **`.default([])` não é frouxidão — é o conserto de um bug vivo.** As sete
   * entradas de `catalogoStatus` no seed da Lambda NÃO tinham esta chave, e o
   * campo era obrigatório aqui: `useCatalogoDeStatusAdm` lançava no zod e a `2p`
   * não renderizava contra o gateway real. Só passava em `npm run dev` porque o
   * fixture do MSW se declara "TRANSCRITO do seed" e ADICIONA o campo que o seed
   * não tem — o mock mentindo a favor.
   *
   * O backend passou a agregá-lo (as transições não são coluna: vivem em
   * `administracao.transicoes_status_oportunidade`). O default fica porque os
   * dois lados implantam SEPARADO: um frontend publicado antes do backend
   * encontraria a resposta antiga, e um catálogo sem grafo de transições degrada
   * (nenhum botão de transição oferecido) em vez de derrubar a tela.
   */
  transicoesPermitidas: z.array(valorDeCatalogo).default([]),

  /**
   * Status que faz parte do ciclo padrão da plataforma (BR-20).
   *
   * 🔒 A `2p` não pode oferecer "excluir" nestes. Apagar `proposta` levaria
   * junto, por CASCADE, todas as arestas de transição que passam por ele — e
   * recriar o status **não** recria o grafo; nada na UI nem na API consegue
   * reconstruí-lo.
   *
   * O backend já recusa com 409 `ITEM_DO_SISTEMA` (a checagem que vale é a
   * dele), mas oferecer um botão que sempre falha é pior que não oferecer.
   *
   * É o irmão do `padrao` de `grupoSchema` — mesma necessidade, e ela existia
   * nos dois agregados desde sempre; só tinha sido resolvida num.
   */
  geridoPeloSistema: z.boolean().default(false),
})

export type StatusDoCatalogo = z.infer<typeof statusDoCatalogoSchema>

export const servicoAwsSchema = z.object({
  /** Casa com `line_item_product_code` do CUR 2.0 — é o que liga uma
   *  oportunidade ao custo real sem tabela de-para. */
  idServico: valorDeCatalogo,
  rotulo: z.string(),
  categoria: z.string().nullable().optional(),
})

export type ServicoAws = z.infer<typeof servicoAwsSchema>

export const origemDiscoverySchema = z.object({
  idOrigem: valorDeCatalogo,
  rotulo: z.string(),
  ordem: z.number(),
})

export type OrigemDiscovery = z.infer<typeof origemDiscoverySchema>

/** O envelope de toda listagem da API de FinOps. */
export function envelopeDeLista<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    itens: z.array(item),
    /** Contagem ANTES da paginação — sem ela não há paginador honesto. */
    total: z.number(),
    pagina: z.number(),
    tamanho: z.number(),
  })
}

/**
 * Erro de negócio da API. `codigo` é estável e é sobre ele que a tela decide o
 * que fazer — a `message` é para o humano ler, e pode mudar sem aviso.
 */
export const erroDeNegocioSchema = z.object({
  codigo: z.string(),
  message: z.string(),
  detalhes: z.record(z.string(), z.unknown()).optional(),
})

export type ErroDeNegocio = z.infer<typeof erroDeNegocioSchema>

/** Um campo que falta para uma transição de status (BR-20). */
export const campoFaltanteSchema = z.object({
  campo: z.string(),
  rotulo: z.string(),
})

export type CampoFaltante = z.infer<typeof campoFaltanteSchema>

export { id }
